// @ts-nocheck

import React, { Suspense, lazy, useEffect } from 'react';
import ReactDOM from 'react-dom/client';
import {
  RouterProvider,
  createBrowserRouter,
  useLoaderData,
  useNavigate,
  useParams,
  Navigate, // Added import for Navigate
} from 'react-router-dom';
import { ReactFlowProvider } from 'reactflow';
import { YDocProvider } from './collaboration/useYDoc';
import HeaderBar, { CurrentPages } from './header-bar/HeaderBar';
import { Box, Typography } from '@mui/material';

import './index.css';
import './resources/fonts.css';
import AssistantRunView from './assistant-run';
import VoucherPage from './pages/VoucherPage';
import OrganisationSettingsPage from './pages/OrganisationSettingsPage';
import useActiveOrganisation from './collaboration/useActiveOrganisation';
import CustomersPage from './pages/CustomersPage'; // New import
import useActiveUser from './collaboration/useActiveUser';
import LoginPage from './pages/LoginPage';
import ChartOfAccountsPage from './pages/ChartOfAccountsPage';
import ReportsPage from './pages/ReportsPage';
import { LedgerPage } from './pages/LedgerPage';
import VoucherDetailsPage from './pages/VoucherDetailsPage';
import DocumentsPage from './pages/DocumentsPage';
import TasksPage from './pages/TasksPage';
import DashboardPage from './pages/DashboardPage';
import InstructionsPage from './pages/InstructionsPage';
import DescriptionPage from './pages/DescriptionPage';
import FilesPage from './pages/FilesPage';
import BankAccountsPage from './pages/BankAccountsPage';

const FlowApp = lazy(() => import('./App'));
const FileUpload = lazy(() => import('./run/upload/FileUpload'));
const KeywordMapApp = lazy(() => import('./keyword-map/KeywordMapApp'));
const Dashboard = lazy(() => import('./dashboard/App'));
const AssistantEditor = lazy(
  () => import('./assistant-editor/AssistantEditor')
);

const TestRedirect = () => {
  const navigate = useNavigate();

  const [activeOrganisation, setActiveOrganisation] = useActiveOrganisation();

  const params = useParams();
  const openNewThread = async () => {
    const response = await fetch(`/api/assistant/${params.id}/threads`, {
      method: 'POST',
      headers: {
        'X-Organisation-Id': activeOrganisation.organizationId,
      },
    });

    const body = await response.json();
    const url = window.location.pathname.replace(
      '/test',
      `/threads/${body.id}`
    );

    navigate(url);
  };

  useEffect(() => {
    openNewThread();
  }, []);

  return <div></div>;
};

const RunLoader = () => {
  const { threadId } = useParams();

  return (
    <Suspense fallback={<div>Loading...</div>}>
      <AssistantRunView threadId={threadId} />
    </Suspense>
  );
};

const AssistantLoader = () => {
  const { assistantId } = useParams();

  console.log(assistantId);

  if (!assistantId) {
    return <div>loading...</div>;
  }

  return (
    <Suspense fallback={<div>Loading...</div>}>
      <YDocProvider docId={assistantId}>
        <AssistantEditor assistantId={assistantId} />
      </YDocProvider>
    </Suspense>
  );
};

const FlowLoader = () => {
  const { flowId } = useParams();

  return (
    <ReactFlowProvider>
      <Suspense fallback={<div>Loading...</div>}>
        <FlowApp flowId={flowId} />
      </Suspense>
    </ReactFlowProvider>
  );
};
const VoucherPageLoader = () => {
  return <VoucherPage />;
};

const DashboardLoader = () => {
  return (
    <Suspense fallback={<div>Loading...</div>}>
      <Dashboard />
    </Suspense>
  );
};

const WordMapLoader = () => {
  const { wordMapId } = useParams();
  return (
    <Suspense fallback={<div>Loading...</div>}>
      <KeywordMapApp keywordMapId={wordMapId as string} />
    </Suspense>
  );
};

const ProtectedRoute = ({ children }) => {
  const { activeUser } = useActiveUser();
  return activeUser ? children : <Navigate to="/login" />;
};

const router = createBrowserRouter([
  {
    path: '/login',
    element: <LoginPage />,
  },
  {
    path: '/',
    element: <Navigate to="/dashboard" />,
  },
  {
    path: '/dashboard',
    element: (
      <ProtectedRoute>
        <Box
          sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}
        >
          <Box component="main" sx={{ flexGrow: 1 }}>
            <DashboardPage />
          </Box>
        </Box>
      </ProtectedRoute>
    ),
  },
  {
    path: '/activity',
    element: (
      <ProtectedRoute>
        <Box
          sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}
        >
          <Box component="main" sx={{ flexGrow: 1 }}>
            <DocumentsPage />
          </Box>
        </Box>
      </ProtectedRoute>
    ),
  },
  {
    path: '/files',
    element: (
      <ProtectedRoute>
        <Box
          sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}
        >
          <Box component="main" sx={{ flexGrow: 1 }}>
            <FilesPage />
          </Box>
        </Box>
      </ProtectedRoute>
    ),
  },
  {
    path: '/tasks',
    element: (
      <ProtectedRoute>
        <Box
          sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}
        >
          <Box component="main" sx={{ flexGrow: 1 }}>
            <TasksPage />
          </Box>
        </Box>
      </ProtectedRoute>
    ),
  },
  {
    path: '/description',
    element: (
      <ProtectedRoute>
        <Box
          sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}
        >
          <Box component="main" sx={{ flexGrow: 1 }}>
            <DescriptionPage />
          </Box>
        </Box>
      </ProtectedRoute>
    ),
  },
  {
    path: '/instructions',
    element: (
      <ProtectedRoute>
        <Box
          sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}
        >
          <Box component="main" sx={{ flexGrow: 1 }}>
            <InstructionsPage />
          </Box>
        </Box>
      </ProtectedRoute>
    ),
  },
  {
    path: '/bank-accounts',
    element: (
      <ProtectedRoute>
        <Box
          sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}
        >
          <Box component="main" sx={{ flexGrow: 1 }}>
            <BankAccountsPage />
          </Box>
        </Box>
      </ProtectedRoute>
    ),
  },
  {
    path: '/settings',
    element: (
      <ProtectedRoute>
        <Box
          sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}
        >
          <Box component="main" sx={{ flexGrow: 1 }}>
            <OrganisationSettingsPage />
          </Box>
        </Box>
      </ProtectedRoute>
    ),
  },
  // Add error boundary for all routes
  {
    path: '*',
    element: (
      <Box sx={{ p: 3 }}>
        <Typography variant="h4" sx={{ fontFamily: 'Inter' }}>Page Not Found</Typography>
        <Typography sx={{ fontFamily: 'Inter' }}>The page you're looking for doesn't exist.</Typography>
      </Box>
    ),
  },
]);

console.log('TEST');
const AuthenticatedApp = () => {
  const { activeUser } = useActiveUser();

  console.log('AUTHENTICATED APP');
  if (!activeUser) {
    return <RouterProvider router={router} />;
  }

  return <RouterProvider router={router} />;
};

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <AuthenticatedApp />
  </React.StrictMode>
);
